import styled from 'types/theme.types';
import { css, useTheme } from '@emotion/react';
import { newDesignOnly } from 'constants/themes.constants';
// Legacy path only — the raw-antd re-export. The new design uses RadioGroup below.
import type { RadioChangeEvent } from 'components/common/bridged-radio-group';
import { Radio } from 'components/common/bridged-radio-group';
import RadioGroup from 'components/common/radio-group';
import { INFINITE_VALUE, INHERIT_FROM_ORG } from 'constants/ttl.constants';
import { FormattedMessage } from 'react-intl';
import type { UseFormReturnType } from 'hooks/use-form.hook';
import type { TtlPolicy } from 'components/policies/common-ttl';
import { INIT_TTL_VALUE } from 'components/policies/common-ttl';

export type TtlType = 'max' | 'default';
export interface TtlDurationRadioButtonProps {
  value: string | null;
  formatMessageId: string;
  name: string;
}

const TtlDurationRadioButton: React.FC<TtlDurationRadioButtonProps> = ({ ...props }) => (
  <Radio value={`${props.value}`} data-e2e={`${props.name}-number-radio-button`}>
    <FormattedMessage id={props.formatMessageId} />
  </Radio>
);

export interface TtlRadioButtonsProps {
  form: UseFormReturnType<TtlPolicy>;
  name: 'maxTtl' | 'defaultTtl';
}

export const TtlRadioButtons: React.FC<TtlRadioButtonsProps> = ({ form, name }) => {
  const theme = useTheme();
  const canSetDefaultToInfinite: boolean = !(name === 'defaultTtl' && form.getValues('maxTtl') !== INFINITE_VALUE);
  const applyChange = (selected: string): void => {
    if (selected === 'infinite') {
      // in case max ttl is not infinite just ignore when trying to switch default TTL to infinite (DefaultTTl <= MaxTTL)
      if (canSetDefaultToInfinite) form.setValue(name, INFINITE_VALUE);
    } else {
      form.setValue(name, INIT_TTL_VALUE);
    }
  };
  const onChange = (e: RadioChangeEvent): void => applyChange(e.target.value);
  const onNewDesignChange = (selected: string): void => applyChange(selected);

  const ttlValue = form.getValues(name);

  /**
   * `checked` is deliberately absent here and was inert where it stood: antd's `Radio.Group` drives
   * selection from the group's own `value` and ignores a child's `checked`. It was also inverted —
   * `ttlValue !== INFINITE_VALUE` is false in the default-infinite case, where
   * project-settings-policies.spec.tsx asserts the button IS checked. Dropping it changes nothing.
   */
  const infiniteButton = (
    <StyledRadioButton value={'infinite'} data-e2e={`${name}-infinite-radio-button`}>
      <FormattedMessage id={`project.settings.policies.ttl.radio.buttons.${name}.value`} />
    </StyledRadioButton>
  );

  const durationButton = (
    <TtlDurationRadioButton
      value={'custom'}
      formatMessageId={'project.settings.policies.custom.ttl'}
      name={name}></TtlDurationRadioButton>
  );

  return (
    <>
      <RadioButtonTitle>
        <FormattedMessage id={`project.settings.policies.ttl.radio.buttons.${name}.title`} />
      </RadioButtonTitle>
      {theme.isNewDesign ? (
        <RadioGroup
          horizontal
          value={ttlValue === INFINITE_VALUE ? 'infinite' : 'custom'}
          onChange={onNewDesignChange}
          disabled={form.isSubmitting || ttlValue === INHERIT_FROM_ORG}
          items={[
            {
              value: 'infinite',
              'data-e2e': `${name}-infinite-radio-button`,
              content: <FormattedMessage id={`project.settings.policies.ttl.radio.buttons.${name}.value`} />
            },
            {
              value: 'custom',
              'data-e2e': `${name}-number-radio-button`,
              content: <FormattedMessage id={'project.settings.policies.custom.ttl'} />
            }
          ]}
        />
      ) : (
        <StyledRadioGroup
          value={ttlValue === INFINITE_VALUE ? 'infinite' : 'custom'}
          onChange={onChange}
          disabled={form.isSubmitting || ttlValue === INHERIT_FROM_ORG}>
          {infiniteButton}
          {durationButton}
        </StyledRadioGroup>
      )}
    </>
  );
};

const StyledRadioButton = styled(Radio)`
  width: 11em;
`;

const RadioButtonTitle = styled.div`
  width: 7em;

  /**
   * The new design's row is a flex container with less slack, so the title shrinks below its 7em
   * basis and "Default TTL:" wraps onto a second line — 64.9px of box for 84.5px of text. The width
   * itself is right in both designs; only the shrink needs stopping, and only on this side.
   */
  ${newDesignOnly(css`
    flex-shrink: 0;
  `)}
`;

const StyledRadioGroup = styled(Radio.Group)`
  display: flex;
`;
